/**
 * project-door.js — window.__projectDoor
 *
 * ОДИН владелец вопроса: «человек пришёл с сохранённым проектом — куда ему
 * нажать?»
 *
 * Класс дефекта, найденный 17.09.2026 на /slideshow-maker и подтверждённый
 * живой пробой у соседей: единственная кнопка «Загрузить проект» живёт в
 * панели инструмента, а панель `display:none`, пока не добавлен первый файл.
 * То есть ровно в том состоянии, в котором человек с файлом проекта и
 * находится, двери НЕТ. Он видит зону загрузки — и кладёт туда сам проект,
 * либо уходит.
 *
 *   Рамиль (Премиум, 17.09.2026, /slideshow-maker): «И на другом компе нет
 *   кнопки загрузить». Замер по тому тулу за 90 дней: 232 сессии сохранили
 *   проект, 11 когда-либо открыли.
 *
 * Дверь живёт РЯДОМ С ЗОНОЙ ЗАГРУЗКИ (там, куда смотрят) и исчезает сама,
 * когда работа открыта — это описывается одним селектором `hide-when`, а не
 * кодом в каждом туле.
 *
 * ⚠️ Зона загрузки — НЕ дверь к проекту, и подменять её нельзя: файл проекта,
 * брошенный в зону, попадёт в разбор медиа-файлов (у /audio-mixer он просто не
 * пройдёт фильтр типа и исчезнет молча). Поэтому у двери свой `<input>` со
 * своим `accept`.
 *
 * Разметка — `<x-project-door>`; тул подключается одной строкой:
 *
 *   window.__projectDoor.mount(el, { onFile: fn, slug: 'audio-mixer' });
 *
 * Примитив держит: показ/скрытие по `hide-when`, клик → пикер, ЖИВОЙ FileList
 * (копия до сброса `value` — иначе файл исчезает), и телеметрию, которой у
 * соседей не было вовсе: `project_door_shown`, `project_door_used`,
 * `project_door_file`. Ноль в базе теперь значит «не пользуются», а не «не
 * измеряли».
 */
(function (w) {
    'use strict';
    // Заглушка из компонента копила вызовы, пока файл ехал; настоящий модуль
    // забирает очередь себе. Второй РЕАЛЬНЫЙ модуль не грузим.
    var queued = (w.__projectDoor && w.__projectDoor._q) ? w.__projectDoor._q : null;
    if (w.__projectDoor && !queued) return;

    function track(name, props) {
        try { if (w.__ae) w.__ae('feature_use', Object.assign({ f: name }, props || {})); } catch (_) {}
    }

    function ext(name) {
        return String(name || '').split('.').pop().toLowerCase().slice(0, 8);
    }

    /** Материал тула, а не файл проекта. */
    var MEDIA_EXT = /^(jpe?g|png|webp|gif|bmp|avif|heic|heif|tiff?|svg|mp4|mov|m4v|webm|mkv|avi|3gp|mpe?g|mp3|wav|m4a|aac|ogg|oga|opus|flac|wma|aiff?)$/;
    function isMedia(f) {
        var t = String((f && f.type) || '').toLowerCase();
        if (t.indexOf('image/') === 0 || t.indexOf('video/') === 0 || t.indexOf('audio/') === 0) return true;
        return MEDIA_EXT.test(ext(f && f.name));
    }

    /**
     * mount(el, opts)
     *   el   — the <x-project-door> root (or a selector)
     *   opts.onFile(file)  — required; the tool reads the project itself
     *   opts.onOther(file) — optional; a file that is NOT a project (a photo,
     *                        a video, an ordinary zip). Without it the tool
     *                        answers «не тот файл»; with it the file goes where
     *                        the person obviously meant it to go.
     *   opts.slug          — telemetry slug (defaults to the page's tool slug)
     *   opts.hideWhen      — CSS selector; while it matches, the door is gone
     *                        (defaults to the element's data-door-hide-when)
     * Returns { show, hide, sync, el } or null.
     */
    /* ⚠️ ПОРЯДОК СКРИПТОВ. Тул может позвать mount() РАНЬШЕ, чем загрузится
       этот файл: оба тега отложенные, а порядок в стеке решает блейд, не мы —
       так у /storyboard-maker дверь отрисовалась и осталась скрытой, потому что
       `window.__projectDoor` в тот момент ещё не существовало. Поэтому mount()
       идемпотентен, повторный вызов лишь доставляет обработчик, а сам файл
       поднимает все двери на странице сам (см. autoMount внизу).
       И дверь НЕ показывается, пока обработчик не зарегистрирован: дверь,
       которая ничего не открывает, хуже её отсутствия. */
    function mount(el, opts) {
        opts = opts || {};
        if (typeof el === 'string') el = document.querySelector(el);
        if (!el) return null;
        if (el.__doorApi) {
            if (opts.onFile) { el.__doorApi.onFile = opts.onFile; el.__doorApi.sync(); }
            if (opts.onOther) el.__doorApi.onOther = opts.onOther;
            if (opts.slug) el.__doorApi.slug = opts.slug;
            return el.__doorApi;
        }

        var btn = el.querySelector('[data-project-door-btn]');
        var input = el.querySelector('[data-project-door-input]');
        var slug = opts.slug || el.getAttribute('data-door-slug')
            || (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
        var hideWhen = opts.hideWhen || el.getAttribute('data-door-hide-when') || '';
        var shown = false;

        function workOpen() {
            if (!hideWhen) return false;
            try { return !!document.querySelector(hideWhen); } catch (_) { return false; }
        }

        function sync() {
            // No handler yet (the tool's script has not run): a door that opens
            // nothing must not invite a click.
            if (workOpen() || !api.onFile) { if (!el.hidden) el.hidden = true; return; }
            if (el.hidden) {
                el.hidden = false;
                /* Работа закрыта/очищена — сохранять в прежний файл больше
                   нельзя: «Сохранить» затёр бы её молча чужим содержимым. */
                forgetOpened(api.slug);
            }
            // «Показано» = у двери ЕСТЬ КОРОБКА. Предок может быть скрыт своим
            // кодом тула (у /storyboard-maker пустой экран прячется целиком), и
            // считать такую дверь показанной значит врать себе — тот же урок,
            // что у стены лимита: обещание обязано ДОЙТИ.
            if (!shown && el.getBoundingClientRect().height > 0) {
                shown = true;
                track('project_door_shown', { slug: slug });
            }
        }

        var api = { el: el, slug: slug, onFile: opts.onFile || null, onOther: opts.onOther || null };
        if (btn && input) {
            /* Открылся ли вообще пикер. `cancel` приходит, когда человек закрыл
               его пустым — это ЕДИНСТВЕННЫЙ способ отличить «выбрал и передумал»
               от «нажал, и ничего не произошло». Повод: одна сессия нажала
               кнопку семь раз за секунду и не выбрала ничего, и объяснить это
               было нечем (тот же дискриминатор уже работает у зоны загрузки). */
            input.addEventListener('cancel', function () {
                track('project_door_cancel', { slug: api.slug });
            });
            btn.addEventListener('click', function () {
                track('project_door_used', { slug: api.slug });
                input.click();
            });
            input.addEventListener('change', function () {
                // ⚠️ `input.files` is LIVE — clearing `value` empties it, so the
                // file is taken BEFORE the reset that re-arms the picker for the
                // same file next time.
                var f = input.files && input.files[0];
                input.value = '';
                if (!f) return;
                track('project_door_file', { slug: api.slug, ext: ext(f.name), sizeKB: Math.round((f.size || 0) / 1024) });
                /* Не проект — не тупик. Замер первых 15 часов: из двух
                   выбранных через дверь файлов ОБА были не проектом (mp4 и
                   архив без проекта внутри), то есть отказ — это самый частый
                   исход двери, а не редкий. Материал уходит туда, куда человек
                   его и нёс: в обычное добавление файлов тула. */
                if (api.onOther && isMedia(f)) { try { api.onOther(f); } catch (e) {} return; }
                if (!api.onFile) return;
                noteOpened(api.slug, f.name);   // сохранять будем сюда же
                try { api.onFile(f); } catch (e) {
                    if (w.__handleError) w.__handleError(e, { stage: 'project_door' });
                }
            });
        }

        /* Как дверь узнаёт, что работа открылась.
         *
         * ⚠️⚠️ НЕ MutationObserver по всему `body`. Первая версия висела на
         * `document.body` с `subtree:true, attributes:true` и звала sync() на
         * КАЖДУЮ мутацию — и это сломало соседний файловый инпут: на странице
         * с такой дверью `setInputFiles` по зоне загрузки вис до таймаута, а
         * живой сторож /audio-mixer (42/42 на main) упал целиком. Причина
         * доказана бисекцией: убрать элемент двери из DOM — и действие
         * проходит; оставить дверь без mount() — тоже проходит. Плюс такой
         * наблюдатель на каждой странице тула — это тот самый холостой тик, за
         * который мы уже платили (CLAUDE.md, «бесконечная анимация»).
         *
         * Здесь достаточно одного querySelector четыре раза в секунду: он не
         * трогает раскладку, ничего не мутирует и не может помешать ни
         * браузеру, ни сторожам. Дверь должна и ПОЯВЛЯТЬСЯ обратно (человек
         * нажал «Очистить всё»), поэтому проверка не выключается. */
        sync();
        var timer = w.setInterval(sync, 250);
        w.addEventListener('pagehide', function () { try { w.clearInterval(timer); } catch (_) {} });

        api.sync = sync;
        el.__doorApi = api;
        return api;
    }

    /** Every door on the page is wired by the module itself — the tool only
        registers what to do with the file, in whatever order it loads. */
    function autoMount() {
        var all = document.querySelectorAll('[data-project-door]');
        for (var i = 0; i < all.length; i++) mount(all[i], {});
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
    else autoMount();

    function drain() {
        if (!queued) return;
        var q = queued; queued = null;
        for (var i = 0; i < q.length; i++) {
            try { mount.apply(null, q[i]); } catch (_) {}
        }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', drain);
    else drain();

    /**
     * matchRows(saved, live, opts) → { pairs: [[liveItem, savedRow]], leftover: [savedRow] }
     *
     * Сажает сохранённые строки проекта на живые объекты ПО ИМЕНИ ФАЙЛА, а не
     * по позиции. Позиция — то, что было у /audio-mixer и /podcast-editor до
     * 18.09.2026, и она неверна по построению: пикер отдаёт файлы в своём
     * порядке (обычно по алфавиту), а проект помнит порядок автора. Громкость,
     * панорама, сдвиги и эффекты садились на ЧУЖИЕ дорожки — молча.
     *
     * Очередь на имя, а не один слот: один и тот же файл законно кладут дважды,
     * и каждая сохранённая строка должна забрать СВОЮ копию (тот же урок, что у
     * /slideshow-maker с повторяющимися фото). Строка, чьё имя не нашлось,
     * уходит в `leftover` — её файл добавят следующей пачкой, и она встанет
     * тогда; выбрасывать её нельзя.
     *
     *   opts.nameOf(item)  — имя живого объекта (по умолчанию item.file.name || item.name)
     *   opts.keyOf(row)    — имя в сохранённой строке (по умолчанию row.fileName)
     *   opts.idOf(item)    — идентичность живого объекта (по умолчанию item.id)
     *   opts.byIndex       — разрешить позиционный запасной вариант для строк
     *                        БЕЗ имени (старые проекты). По умолчанию true.
     */
    function matchRows(saved, live, opts) {
        opts = opts || {};
        var nameOf = opts.nameOf || function (it) { return (it && it.file && it.file.name) || (it && it.name) || ''; };
        var keyOf = opts.keyOf || function (r) { return r && r.fileName; };
        var idOf = opts.idOf || function (it) { return it && it.id; };
        saved = saved || [];
        live = live || [];

        var byName = Object.create(null);
        for (var i = 0; i < live.length; i++) {
            var n = nameOf(live[i]);
            if (!n) continue;
            (byName[n] = byName[n] || []).push(live[i]);
        }
        var used = Object.create(null), pairs = [], leftover = [];
        for (var r = 0; r < saved.length; r++) {
            var row = saved[r];
            var q = byName[keyOf(row)] || [];
            var hit = null;
            while (q.length) {
                var cand = q.shift();
                if (!used[idOf(cand)]) { hit = cand; break; }
            }
            if (!hit && opts.byIndex !== false && !keyOf(row)) {
                var at = live[r];
                if (at && !used[idOf(at)]) hit = at;
            }
            if (hit) { used[idOf(hit)] = 1; pairs.push([hit, row]); }
            else leftover.push(row);
        }
        return { pairs: pairs, leftover: leftover };
    }

    /* ── Сохранение проекта: второй раз — в ТОТ ЖЕ файл ──────────────────
     *
     * `__downloadFile` всегда создаёт НОВЫЙ файл, поэтому у человека, который
     * правит одну колоду несколько дней, в «Загрузках» копятся
     * «slideshow (1).tsmproj», «(2)», «(3)», и он открывает не ту. File System
     * Access умеет отдать ручку файла и писать в неё повторно без диалога.
     *
     * Охват (замер 18.09.2026, RU): настольный Chromium ≈ 47 % сессий за
     * неделю (Yandex 102 402, Chrome 40 963, Edge 31 157, Opera 13 331) — и,
     * в отличие от обработчика файлов PWA, установка не нужна (установлено
     * 0.35 % сессий). Где API нет — обычное скачивание, как было.
     *
     * Ручка живёт СЕССИЮ вкладки: так ведёт себя «Сохранить как…» → «Сохранить»
     * в настольном редакторе, и это не требует ни IndexedDB, ни повторных
     * запросов разрешения при каждом заходе.
     *
     *   await __projectDoor.saveFile(blob, { key:'slideshow', suggestedName, types, slug })
     *     → { via: 'handle' | 'picker' | 'download', name }
     */
    var handles = Object.create(null);
    /* Имя файла, который человек ОТКРЫЛ этой дверью. Ручки у `<input
       type=file>` нет, но имя есть — и его достаточно, чтобы диалог сохранения
       предложил тот же файл, а не новый датированный дубликат рядом. */
    var opened = Object.create(null);

    /** Дверь (или тул со своей дверью) говорит: работа пришла из этого файла. */
    function noteOpened(key, name) {
        if (!key || !name) return;
        /* Открыли ДРУГУЮ работу — прежняя ручка больше не адрес: иначе
           «Сохранить» молча перезапишет тот файл, который человек редактировал
           до этого, а не тот, который открыл сейчас. Самая дорогая из ошибок
           этого класса: потеря чужой работы без единого сообщения. */
        if (opened[key] !== String(name)) handles[key] = null;
        opened[key] = String(name);
    }

    /** Имя файла, из которого открыта работа (тулу — для подписи кнопки). */
    function openedName(key) { return opened[key || 'project'] || ''; }

    /** Работа началась заново — прежний файл больше не адрес сохранения. */
    function forgetOpened(key) {
        if (!key) return;
        opened[key] = null;
        handles[key] = null;
    }

    /** Имя с датой: «Слайдшоу 2026-09-18 10-25.tsmproj». Байтовый потолок и
     *  запрещённые символы — забота общего __outputName, если он на странице. */
    function suggestName(base, extension) {
        var pad = function (n) { return (n < 10 ? '0' : '') + n; };
        var d = new Date();
        var stamp = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
            + ' ' + pad(d.getHours()) + '-' + pad(d.getMinutes());
        var full = String(base || 'project').trim() + ' ' + stamp;
        var on = w.__outputName;
        if (on && on.join) { var j = on.join(full, extension); if (j) return j; }
        return full.replace(/[\\/:*?"<>|]+/g, '-') + '.' + extension;
    }

    function canPickSave() {
        return typeof w.showSaveFilePicker === 'function' && w.isSecureContext !== false;
    }

    async function writeTo(handle, blob) {
        if (handle.queryPermission) {
            var perm = await handle.queryPermission({ mode: 'readwrite' });
            if (perm !== 'granted' && handle.requestPermission) {
                perm = await handle.requestPermission({ mode: 'readwrite' });
            }
            if (perm !== 'granted') return false;
        }
        var stream = await handle.createWritable();
        await stream.write(blob);
        await stream.close();
        if (w.__deliveredToDisk) w.__deliveredToDisk(handle.name, blob, 'save_handle');
        return true;
    }

    async function saveFile(blob, opts) {
        opts = opts || {};
        var key = opts.key || opts.slug || 'project';
        /* Работа, открытая из файла, сохраняется В НЕГО: диалог предлагает то
           же имя, браузер сам спрашивает «заменить?», и человек не заводит
           второй файл там, где хотел обновить первый. Датированное имя от тула
           остаётся для НОВОЙ работы. */
        var name = opened[key] || opts.suggestedName || 'project';

        // Второй и последующие разы: пишем туда же, молча.
        if (handles[key]) {
            try {
                if (await writeTo(handles[key], blob)) {
                    track('project_saved_in_place', { slug: opts.slug || '', bytes: blob.size });
                    return { via: 'handle', name: handles[key].name || name };
                }
            } catch (e) {
                // Файл удалили, диск отсоединили, разрешение отозвали — забываем
                // ручку и спрашиваем заново, а не молча теряем сохранение.
                handles[key] = null;
                track('project_save_handle_lost', { slug: opts.slug || '', err: String((e && e.name) || e).slice(0, 40) });
            }
        }

        if (canPickSave()) {
            try {
                var handle = await w.showSaveFilePicker({
                    suggestedName: name,
                    types: opts.types || undefined,
                });
                if (await writeTo(handle, blob)) {
                    handles[key] = handle;
                    track('project_saved_picked', { slug: opts.slug || '', bytes: blob.size });
                    return { via: 'picker', name: handle.name || name };
                }
            } catch (e) {
                // AbortError = человек закрыл диалог. Это решение, а не сбой:
                // ничего не скачиваем следом, иначе «отмена» кладёт файл.
                if (e && e.name === 'AbortError') {
                    track('project_save_cancelled', { slug: opts.slug || '' });
                    return { via: 'cancelled', name: '' };
                }
                track('project_save_picker_failed', { slug: opts.slug || '', err: String((e && e.name) || e).slice(0, 40) });
            }
        }

        if (w.__downloadFile) w.__downloadFile(blob, name, { src: 'project_save' });
        track('project_saved_download', { slug: opts.slug || '', bytes: blob.size });
        return { via: 'download', name: name };
    }

    /** Есть ли уже ручка — тул может сказать «Обновить», а не «Сохранить». */
    function hasHandle(key) { return !!handles[key || 'project']; }

    w.__projectDoor = { mount: mount, matchRows: matchRows, saveFile: saveFile, noteOpened: noteOpened,
        openedName: openedName, forgetOpened: forgetOpened,
        hasHandle: hasHandle, canPickSave: canPickSave, suggestName: suggestName };
})(window);
